import { Component, Input, OnChanges, SimpleChanges, inject, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { UserApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/user/user-api';
import { UpdateUserRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/user/update-user.request';
import { Language } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/language.enum';
import { BaseToastComponent } from '../../../@commons/ui-components/toast-popup/base-toast-component';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { ListRowComponent } from '../../../@commons/ui-components/list-row/list-row.component';
import { ListSectionComponent } from '../../../@commons/ui-components/list-row/list-section.component';
import {
  APP_LANGUAGES,
  AppLanguageService,
  LANGUAGE_NATIVE_NAMES,
} from '../../../@commons/@services/front/app-language.service';

/**
 * Feuille « Langue de l'application » : une ligne par langue, la langue du
 * compte cochée. Toucher une langue l'enregistre sur le compte, bascule l'app
 * et referme la feuille : pas de bouton « Enregistrer » pour un seul choix.
 *
 * Refusée par le serveur, la langue ne change pas : la feuille reste ouverte,
 * le message d'erreur s'affiche et la coche revient sur l'ancienne langue.
 */
@Component({
  selector: 'vw-language-toast',
  imports: [SharedModule, TranslatePipe, ListSectionComponent, ListRowComponent],
  templateUrl: './language-toast.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: UserApi,
      useClass: UserApi,
    },
  ],
})
export class LanguageToastComponent extends BaseToastComponent implements OnChanges {
  /** Langue du compte (`preferredLanguage`), ou null : rien n'est coché. */
  @Input() current: string | null = null;

  readonly languages = APP_LANGUAGES;
  readonly names = LANGUAGE_NATIVE_NAMES;
  /** Langue en cours d'enregistrement : cochée tout de suite, retirée en cas d'échec. */
  saving: string | null = null;
  error = false;

  private readonly appLanguage = inject(AppLanguageService);

  constructor(private userApi: UserApi) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue) this.error = false;
  }

  get checked(): string | null {
    return this.saving ?? this.current;
  }

  async choose(language: string): Promise<void> {
    if (this.saving) return;
    if (language === this.current) {
      this.onClose();
      return;
    }
    this.saving = language;
    this.error = false;
    try {
      const request = new UpdateUserRequest();
      // L'énumération du SDK ne connaît pas encore IT et ES ; l'API, si.
      request.preferredLanguage = language as Language;
      await this.userApi.update(request);
      this.appLanguage.apply(language);
      this.onClose({ preferredLanguage: language });
    } catch (e) {
      this.error = true;
      console.error('LanguageToastComponent: failed to update language', e);
    } finally {
      this.saving = null;
    }
  }
}
